import type { Customer, CustomerId, MenuItem, Recipe } from '../types';
import { qualityGrade, type QualityGrade } from './MixingSystem';

// Satisfaction scoring per the approved design (docs/game-design.md).
// Range clamped to −30..+30 per serve. The batch's quality grade (mix
// accuracy × bake timing) folds doneness and measuring into one modifier.
const GRADE_MODIFIER: Record<QualityGrade, number> = {
  perfect: 5,
  good: 0,
  okay: -10,
  bad: -25,
};

class CustomerSystem {
  scoreServe(batch: MenuItem, recipe: Recipe, customer: Customer): number {
    const { prefs } = customer;
    let s = 0;

    const typeIdx = prefs.itemTypes.indexOf(recipe.itemType);
    if (typeIdx >= 0) s += Math.max(4, 10 - typeIdx * 3);      // +10/+7/+4

    const flavIdx = prefs.flavors.indexOf(recipe.flavor);
    if (flavIdx >= 0) s += Math.max(4, 12 - flavIdx * 4);      // +12/+8/+4

    if (prefs.favoriteTopping && batch.toppings.includes(prefs.favoriteTopping)) s += 8;

    if (prefs.dislikedIngredient &&
        recipe.ingredients.some((i) => i.ingredient === prefs.dislikedIngredient)) s -= 15;

    if (batch.price > prefs.priceTolerance) s -= 10;
    else if (batch.price < prefs.priceTolerance * 0.7) s += 3;

    s += GRADE_MODIFIER[qualityGrade(batch.mixAccuracy, batch.doneness)];

    return Math.max(-30, Math.min(30, s));
  }

  // Cumulative satisfaction drives how likely a regular is to come back.
  // Used by Phase 3's roster sampling; Phase 2 seeds deterministically.
  returnLikelihood(c: Customer): number {
    return Math.min(0.95, Math.max(0.02, 0.15 + c.cumulativeSatisfaction / 200));
  }

  // Deterministic per-day sample: same day always seats the same queue, so a
  // mid-day reload doesn't reroll the customers. Day 1 seats 6, growing to 8.
  seedDayQueue(roster: Customer[], day: number): CustomerId[] {
    const size = Math.min(roster.length, 5 + Math.min(day, 3));
    return [...roster]
      .sort((a, b) => hashId(`${day}:${a.id}`) - hashId(`${day}:${b.id}`))
      .slice(0, size)
      .map((c) => c.id);
  }

  orderText(customer: Customer): string {
    const flavor = customer.prefs.flavors[0] ?? 'yummy';
    const itemType = customer.prefs.itemTypes[0] ?? 'treat';
    const base = `I'd like a ${flavor} ${itemType}!`;
    return customer.prefs.favoriteTopping
      ? `${base}\n(I love ${customer.prefs.favoriteTopping.replace(/-/g, ' ')}!)`
      : base;
  }

  reactionFor(entry: { satisfaction: number; served: boolean }): { emoji: string; text: string; color: string } {
    if (!entry.served) return { emoji: '\u{1F6AA}', text: 'walked away', color: '#888888' };
    const s = entry.satisfaction;
    if (s >= 15) return { emoji: '\u{1F929}', text: 'loved it!', color: '#1f8b3a' };
    if (s >= 5)  return { emoji: '\u{1F60A}', text: 'liked it', color: '#4a9a2f' };
    if (s >= -5) return { emoji: '\u{1F610}', text: 'shrugged', color: '#d9a520' };
    if (s >= -15) return { emoji: '\u{1F620}', text: 'frowned', color: '#c47816' };
    return { emoji: '\u{1F922}', text: 'hated it!', color: '#cc4444' };
  }
}

export function hashId(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export const customersSystem = new CustomerSystem();
